import Icon from "@components/ui/icon";
import { cn } from "@lib/cn";
import { useEffect, useMemo, useRef, useState } from "react";
import type { IMediaFolder } from "@/shared/types";

interface IProps {
  className?: string;
  folders: IMediaFolder[];
  selectedFolderId: string | null;
  setSelectedFolderId: (id: string | null) => void;

  size?: "base" | "lg";
  isArchive?: boolean;
}

export const MediaLibFoldersBreadcrumbs = ({
  size = "base",
  className,
  folders: allFolders,
  selectedFolderId,
  setSelectedFolderId,
  isArchive,
}: IProps) => {
  const containerRef = useRef<HTMLOListElement>(null);
  const [visibleItems, setVisibleItems] = useState<(IMediaFolder | null)[]>([]);

  const selectedFolder = useMemo(() => {
    return selectedFolderId
      ? allFolders.find((f) => f._id === selectedFolderId)
      : null;
  }, [allFolders, selectedFolderId]);

  const foldersPath = useMemo(() => {
    if (!selectedFolder) return [];

    const paths: IMediaFolder[] = [selectedFolder];

    if (!selectedFolder.parentId) return paths;

    let parent = allFolders.find((el) => el._id === selectedFolder.parentId);
    let limit = 1000;
    while (parent && limit > 0) {
      paths.unshift(parent);
      parent = allFolders.find(
        (el) => el._id !== parent?._id && el._id === parent?.parentId,
      );

      limit--;
    }

    return paths;
  }, [allFolders, selectedFolder]);

  // Calculate which items to show based on available space
  useEffect(() => {
    if (!containerRef.current) return;

    const calculateVisibleItems = () => {
      const container = containerRef.current;
      if (!container) return;

      // Get container width
      const width = container.getBoundingClientRect().width;
      // setContainerWidth(width)

      // Always show first and last items
      if (foldersPath.length <= 2) {
        setVisibleItems(foldersPath);
        return;
      }

      // Start with first and last items
      const firstItem = foldersPath[0];
      const lastItem = foldersPath[foldersPath.length - 1];

      // Calculate approximate width needed for first and last items
      // This is a rough estimate - in a real app you'd measure actual rendered widths
      const firstItemWidth = firstItem.title.length * 10 + 67; // 50px for icon and padding
      const lastItemWidth = lastItem.title.length * 10 + 30; // 30px for padding
      const separatorWidth = 17; // Width of separator
      const ellipsisWidth = 67; // Width of ellipsis item

      // Calculate remaining width for middle items
      const remainingWidth =
        width - firstItemWidth - lastItemWidth - 2 * separatorWidth;

      if (remainingWidth < ellipsisWidth) {
        // Not enough space for anything but first and last with ellipsis
        setVisibleItems([firstItem, null, lastItem]);
        return;
      }

      // Try to fit middle items
      const middleItems: (IMediaFolder | null)[] = [];
      let totalMiddleWidth = 0;

      for (let i = 1; i < foldersPath.length - 1; i++) {
        const item = foldersPath[i];
        const itemWidth = item.title.length * 10 + 30 + separatorWidth;

        if (totalMiddleWidth + itemWidth <= remainingWidth) {
          middleItems.push(item);
          totalMiddleWidth += itemWidth;
        } else {
          // No more space for middle items
          if (i < foldersPath.length - 2) {
            // If we couldn't show all middle items, add ellipsis
            middleItems.push(null);
          }
          break;
        }
      }

      setVisibleItems([firstItem, ...middleItems, lastItem]);
    };

    calculateVisibleItems();

    window.addEventListener("resize", calculateVisibleItems);
    return () => {
      window.removeEventListener("resize", calculateVisibleItems);
    };
  }, [foldersPath]);

  if (foldersPath.length === 0) return null;

  return (
    <div
      className={cn(
        "tw-relative tw-m-0 tw-grid tw-list-none tw-grid-cols-[auto_1fr] tw-items-center tw-justify-start tw-overflow-hidden",
        className,
      )}
    >
      <div className="tw-mr-2 tw-flex tw-items-center tw-space-x-2">
        <Icon
          icon="ri-folders-line"
          className="tw-text-[16px]/none tw-text-blue-500"
        />
        <button
          type="button"
          className={cn("tw-text-blue-500 tw-outline-none", {
            "tw-text-[11px] tw-font-medium": size === "base",
            "tw-text-[16px] tw-font-semibold": size === "lg",
          })}
          onClick={() => setSelectedFolderId(null)}
        >
          {isArchive ? "Archive" : "My files"}
        </button>
      </div>
      <ol
        className="tw-relative tw-m-0 tw-grid tw-w-full tw-list-none tw-grid-flow-col tw-justify-start tw-overflow-hidden"
        ref={containerRef}
      >
        {visibleItems.map((f, idx) => (
          <li
            key={f?._id ?? `ellipsis-${idx}`}
            className="tw-mr-2 tw-grid tw-grid-cols-[auto_1fr] tw-items-center tw-space-x-2"
          >
            <Icon
              icon="ri-arrow-right-s-line"
              className="tw-text-[16px]/none tw-text-blue-500"
            />
            {f ? (
              <>
                <button
                  type="button"
                  className={cn(
                    "tw-max-w-full tw-overflow-hidden tw-text-ellipsis tw-text-nowrap tw-outline-none",
                    idx === visibleItems.length - 1
                      ? "tw-text-blue-base"
                      : "tw-text-blue-500",
                    {
                      "tw-text-[11px] tw-font-medium": size === "base",
                      "tw-text-[16px] tw-font-semibold": size === "lg",
                    },
                  )}
                  onClick={() => setSelectedFolderId(f._id)}
                >
                  {f.title}
                </button>
              </>
            ) : (
              <span className="tw-text-[11px] tw-font-medium tw-leading-none tw-text-blue-500">
                ...
              </span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};
